import type {
  Campaign,
  CampaignOption,
  CampaignRow,
  CampaignWithContact,
  TransformedCampaignDetail,
} from '../types/campaign.types';
import { resolveCampaignStatusAndProgress } from './campaign.service';
import { formatCurrency } from '@/utils/currency';

export const UNASSIGNED_BRAND = 'Unassigned';
export const UNASSIGNED_OWNER = 'Unassigned';

interface JoinedBrand {
  id?: string;
  name?: string;
  logo_url?: string | null;
  industry?: string | null;
}

interface JoinedContact {
  id?: string;
  name?: string;
  email?: string | null;
}

export interface RawCampaignWithJoins extends CampaignRow {
  brands?: JoinedBrand | JoinedBrand[] | null;
  contacts?: JoinedContact | JoinedContact[] | null;
}

/**
 * Maps a raw Supabase campaigns query row with joined relations to the canonical Campaign view model
 */
export const mapDbRowToCampaign = (
  campaign: RawCampaignWithJoins,
  brandMap: Record<string, { id: string; name: string; logo_url?: string | null }> = {},
  userMap: Record<string, string> = {},
  fallbackBrandName?: string
): Campaign => {
  const brandFromRelation = Array.isArray(campaign.brands) ? campaign.brands[0] : campaign.brands;
  const contactObj = Array.isArray(campaign.contacts) ? campaign.contacts[0] : campaign.contacts;

  // Resolve Brand Name and Logo without raw UUID fallback
  const brandFromMap = campaign.brand_id
    ? brandMap[campaign.brand_id]
    : (campaign.organization_id ? brandMap[`org_${campaign.organization_id}`] : null);
  let resolvedBrandName = brandFromRelation?.name || brandFromMap?.name || '';

  if (!resolvedBrandName && campaign.brand_id) {
    const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      campaign.brand_id
    );
    if (!isUuid) {
      resolvedBrandName = campaign.brand_id;
    }
  }
  if (!resolvedBrandName && campaign.organization_id && brandMap[`org_${campaign.organization_id}`]) {
    resolvedBrandName = brandMap[`org_${campaign.organization_id}`].name;
  }
  if (!resolvedBrandName && fallbackBrandName) {
    resolvedBrandName = fallbackBrandName;
  }
  if (!resolvedBrandName) {
    resolvedBrandName = UNASSIGNED_BRAND;
  }

  const resolvedBrandLogo = brandFromRelation?.logo_url || brandFromMap?.logo_url || null;
  const creatorName = campaign.created_by ? userMap[campaign.created_by] : null;
  const ownerName = contactObj?.name || creatorName || UNASSIGNED_OWNER;

  // Resolve Dates
  const resolvedStartDate = campaign.start_date || campaign.created_at || null;
  let resolvedEndDate = campaign.end_date || null;
  if (!resolvedEndDate && resolvedStartDate) {
    const d = new Date(resolvedStartDate);
    if (!isNaN(d.getTime())) {
      d.setDate(d.getDate() + 30);
      resolvedEndDate = d.toISOString().split('T')[0];
    }
  }

  // Synchronize Status and Progress
  const { status: resolvedStatus, progress: progressPercent } = resolveCampaignStatusAndProgress(
    resolvedStartDate,
    resolvedEndDate,
    campaign.status
  );

  return {
    id: campaign.id,
    name: campaign.name,
    brand: resolvedBrandName,
    brand_id: campaign.brand_id,
    brand_name: resolvedBrandName,
    brand_logo: resolvedBrandLogo,
    budget: (campaign.total_budget || 0).toString(),
    total_budget: campaign.total_budget,
    spent_budget: campaign.spent_budget,
    influencers: 0,
    deadline: resolvedEndDate || '',
    start_date: resolvedStartDate,
    end_date: resolvedEndDate,
    reach: '0',
    status: resolvedStatus,
    kanban_stage: (campaign.kanban_stage as Campaign['kanban_stage']) || null,
    owner_name: ownerName,
    linked_contact_id: campaign.linked_contact_id,
    created_by: campaign.created_by,
    created_at: campaign.created_at,
    progress_percent: progressPercent,
  };
};

/**
 * Maps raw CampaignWithContact into TransformedCampaignDetail expected by detail components
 */
export const mapDbRowToCampaignDetail = (
  campaign: CampaignWithContact,
  userCountry: string | null = null,
  influencerCount = 0
): TransformedCampaignDetail => {
  const resolvedStartDate = campaign.start_date || campaign.created_at;
  let resolvedEndDate = campaign.end_date || null;
  if (!resolvedEndDate && resolvedStartDate) {
    const fallbackEndDate = new Date(resolvedStartDate);
    if (!Number.isNaN(fallbackEndDate.getTime())) {
      fallbackEndDate.setDate(fallbackEndDate.getDate() + 30);
      resolvedEndDate = fallbackEndDate.toISOString().split('T')[0];
    }
  }

  const lifecycle = resolveCampaignStatusAndProgress(
    resolvedStartDate,
    resolvedEndDate,
    campaign.status
  );

  return {
    id: campaign.id,
    name: campaign.name,
    status: lifecycle.status,
    linked_contact_id: campaign.linked_contact_id,
    brand: campaign.brands
      ? {
          id: campaign.brands.id,
          name: campaign.brands.name,
          industry: campaign.brands.industry || 'Not specified',
          logo_url: campaign.brands.logo_url || undefined,
        }
      : {
          id: 'unknown',
          name: 'Unknown Brand',
          industry: 'Not specified',
          logo_url: undefined,
        },
    platforms: ['instagram', 'youtube'],
    startDate: resolvedStartDate || 'Not set',
    endDate: resolvedEndDate || 'Not set',
    totalBudget: campaign.total_budget ? formatCurrency(campaign.total_budget, userCountry) : 'Not set',
    spentBudget: campaign.spent_budget
      ? formatCurrency(campaign.spent_budget, userCountry)
      : formatCurrency(0, userCountry),
    remainingBudget:
      campaign.total_budget && campaign.spent_budget
        ? formatCurrency(campaign.total_budget - campaign.spent_budget, userCountry)
        : 'Not calculated',
    influencerCount,
    description: campaign.description || 'No description provided',
    objective: 'Drive brand awareness',
    deliverables: 'Content deliverables',
    kanban_stage: campaign.kanban_stage,
  };
};

/**
 * Maps a campaign row or entity to a lean option for pickers and filters
 */
export const mapToCampaignOption = (campaign: {
  id: string;
  name: string;
  brand_name?: string;
  brand?: string;
  status?: string;
}): CampaignOption => ({
  id: campaign.id,
  name: campaign.name,
  brandName: campaign.brand_name || campaign.brand,
  status: campaign.status,
});
